import { useEffect, useState } from "react";

/**
 * Owner-requested (2026-08-28) — `GlobalTopBar.tsx`'s own second line shows
 * the current page's title, reusing the exact string every route already
 * writes to `document.title` (the " — PropDesk" suffix convention every
 * route's own `useEffect(() => { document.title = ... })` already follows)
 * rather than inventing a second, parallel "announce your title" mechanism
 * a route would have to remember to call. A `MutationObserver` on the real
 * `<title>` node is what makes this reactive to route changes at all —
 * `document.title` itself has no change event.
 */
const TITLE_SUFFIX = " — PropDesk";

function currentTitle(): string {
  const raw = document.title;
  return raw.endsWith(TITLE_SUFFIX) ? raw.slice(0, -TITLE_SUFFIX.length) : raw;
}

export function useDocumentTitle(): string {
  const [title, setTitle] = useState(currentTitle);

  useEffect(() => {
    const target = document.querySelector("title");
    if (!target) {
      return;
    }
    const observer = new MutationObserver(() => setTitle(currentTitle()));
    observer.observe(target, { childList: true, characterData: true, subtree: true });
    // A route that already committed its title before this effect first
    // subscribed (mount ordering, not just future changes) is still caught
    // here — `useState(currentTitle)`'s own initializer already covers the
    // very first render, but a fast route change between that render and
    // this effect's subscription would otherwise be missed.
    setTitle(currentTitle());
    return () => observer.disconnect();
  }, []);

  return title;
}
